"use client";
import React from "react";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import { useDispatch } from "react-redux";
import { openRequestCallback } from "@/redux/features/requestCallback/requestCallbackSlice";
import Breadcrumb from "@/app/mutual-fund/portfolio-overlap/overlap-component/ui/Breadcrum";
import { AdvisoryCardProps } from "@/type/servicesPageTypes";

type Heading = {
  heading: string
  subHeading: string
  BreadCrumb: { label: string; url: string | undefined }[]
}

export const MutualFundAdvisory = ({ heading, subHeading, BreadCrumb }: Heading) => {
  const dispatch = useDispatch();
  const cards: AdvisoryCardProps[] = [
    {
      title: "Review & Fix My Investments",
      desc: "Get a clear picture of what's underperforming, overlapping, or misaligned - and exactly what to do next",
      img: "/next_assets/images/portfolio-services/glass.png",
      rotate: "xl:-rotate-3",
      gradient:
        "bg-[linear-gradient(180deg,_rgba(96,26,170,0.1)_0%,_rgba(26,81,170,0)_100%)] xl:shadow-[0px_10px_33px_-7px_#00000026]",
    },

    {
      title: "Build Me a Portfolio from Scratch ",
      desc: "Build a fresh, goal-aligned portfolio structure from scratch.",
      img: "/next_assets/images/portfolio-services/graph.png",
      gradient: "bg-[#F6F6F6]/50  shadow-[0px_10px_33px_-7px_#00000026]",
    },
    {
      title: "Invest Right from the Very First Step ",
      desc: "Start your journey with a certified advisor guiding you before you invest.",
      img: "/next_assets/images/portfolio-services/compass.png",
      rotate: "xl:rotate-3",
      gradient:
        "bg-[linear-gradient(180deg,_rgba(96,26,170,0.1)_0%,_rgba(26,81,170,0)_100%)] xl:shadow-[0px_10px_33px_-7px_#00000026]",
    },
  ];



  return (
    <section className="mb-8 xl:mb-24 ">
      {/* <div className="h-16 bg-[#0D3F91]" /> */}

      <div className="relative">
        <div
          className="w-full     bg-[linear-gradient(#0d3f91_7%,rgba(26,81,170,0)_37%)] lg:bg-[linear-gradient(180deg,#0D3F91_0%,rgba(26,81,170,0)_100%)] xs:pt-20 pt-18">
          <Breadcrumb items={BreadCrumb} />
          <div className="flex justify-center px-4">
            <div className="xs:pt-8 pt-4 max-w-190 text-center">
              <div>
                <h1
                  className="font-bold text-2.5xl md:text-3xl lg:text-5xl text-white"
                  dangerouslySetInnerHTML={{ __html: heading }}
                />
                <p className="text-[#FFFFFF]/80 text-sm sm:text-xl max-w-152.5 mx-auto mt-4">
                  {subHeading}
                </p>
              </div>
              <div className="flex justify-center gap-3 xl:gap-8 mt-6 xl:mt-8">
                {/* Button 1 */}
                <button
                  onClick={() => {
                    document.getElementById("mf-guidance")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                  className="w-1/2  max-w-68 flex items-center justify-center text-center xs:gap-2 text-white text-xs xxs:text-lg font-semibold transition-all duration-300 hover:bg-white hover:text-black bg-primaryBlue py-3 border-2 rounded-2xl"
                >
                  Choose Your Service
                  <Icon.rightArrow className="hidden xs:block" />
                </button>

                <button
                  onClick={() =>  
                    dispatch(
                      openRequestCallback({
                        buttonType: "advisory_call",
                        reason: "Mutual Fund Advisory Call",
                      }),
                    )
                  }
                  className="w-1/2 max-w-68  text-white py-3  text-xs xxs:text-lg bg-white/15 border-2 rounded-2xl hover:bg-primaryBlue font-semibold flex items-center justify-center text-center transition-all duration-300"
                >
                  Book a Call with us
                </button>
              </div>
            </div>
          </div>
          <div>
            <div
              className="flex xl:justify-center xl:items-center xl:gap-x-10 gap-x-5 overflow-x-auto px-4 pb-12"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {cards.map((card, id) => (
                <div
                  key={card.title}
                  className={`min-w-77 2md:max-w-91.25 w-full rounded-6xl xl:rounded-10xl border lg:border-2 border-[#FFFFFF]  py-5 px-5 xl:px-7 xl:py-7 mt-8 ${card.rotate} ${card.gradient} ${id == 1 ? "xl:mt-12" : "xl:mt-16"}`}>
                  <div>
                    <Image
                      src={card.img}
                      alt="icon"
                      className={` ${"mb-4 w-10 h-10 xl:w-17.5 xl:h-17.5 "}`}
                      width={70}
                      height={70}
                    />
                  </div>
                  <p
                    className={` font-semibold  text-xl xl:text-2xl leading-1.3 xl:leading-1.5 text-[#101828] mb-4`}
                  >
                    {card.title}
                  </p>

                  <p className="text-sm xl:text-base xl:leading-6 leading-1.3 min-h-18 text-[#4A5565] ">
                    {card.desc}
                  </p>
                  <button
                    onClick={() => {
                      document.getElementById("mf-guidance")?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });

                      let targetIndex = 0;
                      if (id === 0) targetIndex = 2;
                      else if (id === 1) targetIndex = 0;
                      else if (id === 2) targetIndex = 3;

                      window.dispatchEvent(new CustomEvent('highlightMfGuidanceCard', { detail: targetIndex }));
                    }}
                    className="hover:bg-primaryBlue  hover:text-white transition-all duration-300 mx-auto flex mt-4 xl:mt-6 rounded-xl px-16 w-full xl:px-20 py-3 gap-2 items-center justify-center border border-[#D4D4D4] bg-gray-50 text-sm xl:text-base"
                  >
                    View Options{" "}
                    <Icon.dropdownDownArrow className="-rotate-90" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
